import {
  addElement,
  defaultGenericElement,
  reversionElement,
  updateElement,
} from '../../lib/element';
import { history } from '../../lib/history';
import { type Plugin } from '../../lib/plugin';
import { renderInteractiveScene, renderStaticScene } from '../../lib/scene';
import { isInsideTransformHandle } from '../../lib/selection';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import {
  type ElementMap,
  updateEditorStore,
  useEditorStore,
} from '../../stores/useEditorStore';
import { hexToRgbString } from '../../utils/color';
import { ensureLoadedImage } from '../../utils/image';
import {
  distance,
  normalizeRadians,
  pointRotateRads,
  type Point,
} from '../../utils/math';
import { poly, type GenericPolyElement } from '../poly';
import { getPolyElementAbsoluteCoords } from '../poly/utils/coords';
import { getPathFromPoints } from '../poly/utils/path';
import { getPolyShape } from '../poly/utils/shape';

import {
  getCornerLineAtPercentage,
  getCornerLineIndexAtPoint,
  getPercentageInSegment,
  getSegmentIndexAtPoint,
} from './utils/cornerLines';
import { drawGrid } from './utils/gridLineUtils';
import {
  getGridBounds,
  getGridTransformHandles,
  getPathFromGrid,
  normalizeGridToBounds,
  translateGridCorner,
} from './utils/transform';
export { type CornerGridGeometry } from './utils/geometry';
export { getSegmentPoints } from './utils/cornerLines';

export const TEST_STRUCTURE_REAL_SIZE = 50;
export const CORNER_LINE_DELETE_THRESHOLD = 12;

export type GridPlugin = Plugin<
  GenericPolyElement,
  typeof gridActions,
  typeof gridState
>;

export interface GridElement extends GenericPolyElement {
  type: 'grid';
  calculatedImage?: HTMLImageElement;
  calculatedTestStructure?: HTMLImageElement;
  fillMaterial: {
    src: string;
    image: HTMLImageElement;
    textureRealX: number;
    textureRealY: number;
  } | null;
  gridScale: {
    x: number;
    y: number;
  };
  cornerLines?: [Point, Point][];
  showTestStructure: boolean;
  hideDefaultGrid?: boolean;
}

export interface CornerLinePreview {
  gridId: string;
  top: Point;
  bottom: Point;
}

export interface CornerLineDeletePreview {
  gridId: string;
  lineIndex: number;
}

const gridState = {
  newStrokeColor: '#3c87f7',
  newStrokeWidth: 4,
  cornerLinePreview: null as CornerLinePreview | null,
  cornerLineDeletePreview: null as CornerLineDeletePreview | null,
};

const gridActions = {
  createOrShow: async (
    gridId: string,
    newGridOptions?: {
      textureUrl?: string;
      textureRealX?: number;
      textureRealY?: number;
      startX?: number;
      startY?: number;
      width?: number;
      height?: number;
      isVisible?: boolean;
    },
  ) => {
    const existingGrid = useEditorStore.getState().elements.get(gridId);

    if (existingGrid != null) {
      const elements = useEditorStore.getState().elements;

      updateElement(elements, existingGrid, {
        isVisible: !existingGrid.isVisible,
      });
      renderStaticScene();
      renderInteractiveScene();
      return existingGrid.id;
    }

    const width = newGridOptions?.width ?? 400;
    const height = newGridOptions?.height ?? 400;

    const newElement: GridElement = {
      ...defaultGenericElement(),
      id: gridId,
      originX: newGridOptions?.startX ?? 900,
      originY: newGridOptions?.startY ?? 500,
      points: [
        [0, 0],
        [width, 0],
        [width, height],
        [0, height],
      ],
      path: new Path2D(),
      strokeColor: '#3c87f7',
      strokeWidth: gridState.newStrokeWidth,
      type: 'grid',
      x: newGridOptions?.startX ?? 900,
      y: newGridOptions?.startY ?? 500,
      fillMaterial: null,
      gridScale: {
        x: 400,
        y: 250,
      },
      layer: Number.MAX_SAFE_INTEGER,
      showTestStructure: false,
      isVisible: newGridOptions?.isVisible ?? false,
    };

    if (newGridOptions?.textureUrl) {
      const img = await ensureLoadedImage(
        newGridOptions.textureUrl,
        'anonymous',
      );

      newElement.fillMaterial = {
        src: newGridOptions.textureUrl,
        image: img,
        textureRealX: newGridOptions.textureRealX ?? 50,
        textureRealY: newGridOptions.textureRealY ?? 80,
      };
    }

    const { x1, y1, x2, y2 } = getPolyElementAbsoluteCoords(newElement);

    newElement.width = distance(x1, x2) + gridState.newStrokeWidth;
    newElement.height = distance(y1, y2) + gridState.newStrokeWidth;
    newElement.shape = getPolyShape(newElement);

    addElement(useEditorStore.getState().elements, newElement);
    history.push(updateEditorStore({ selectedElementIds: [newElement.id] }));

    updateRuntimeStore({
      activeTool: 'selection',
    });

    renderStaticScene();
    renderInteractiveScene();
    return newElement.id;
  },
  resetGridToDefault: async (grid: GridElement) => {
    const imageElement = Array.from(
      useEditorStore.getState().elements.values(),
    ).find((e) => e.type === 'image');

    const gridParams = {
      startX: imageElement ? imageElement.x + imageElement.width / 4 : 0,
      startY: imageElement ? imageElement.y + imageElement.height / 4 : 0,
      width: imageElement ? imageElement.width / 2 : 500,
      height: imageElement ? imageElement.height / 2 : 300,
    };

    const width = gridParams.width
      ? gridParams.width + grid.strokeWidth
      : grid.width;
    const height = gridParams.height
      ? gridParams.height + grid.strokeWidth
      : grid.height;
    const x = gridParams?.startX ?? grid.originX ?? grid.x;
    const y = gridParams?.startY ?? grid.originY ?? grid.y;

    const updatedElement: Partial<GridElement> = {
      originX: x,
      originY: y,
      points: [
        [0, 0],
        [width, 0],
        [width, height],
        [0, height],
      ],
      x,
      y,
      width: width,
      height: height,
      cornerLines: undefined,
      bezierHandles: undefined,
      gridScale: {
        x: 400,
        y: 250,
      },
    };

    updatedElement.shape = getPolyShape({ ...grid, ...updatedElement });
    updatedElement.version = {
      ...grid.version,
      ...reversionElement({ ...grid, ...updatedElement }, ['bounds', 'canvas'])
        .version,
    };

    updateElement(
      useEditorStore.getState().elements,
      grid,
      updatedElement,
      true,
    );
    renderInteractiveScene();
  },
  setStrokeColor: (color: string) => {
    gridState.newStrokeColor = color;
  },
  setStrokeWidth: (width: number) => {
    gridState.newStrokeWidth = width;
  },
  setMaterial: async (
    el: GridElement,
    material: {
      imageUrl: string;
      textureRealX: number;
      textureRealY: number;
    } | null,
  ) => {
    const elements = useEditorStore.getState().elements;
    let element = elements.get(el.id) as GridElement;

    if (material == null) {
      updateElement(elements, element, {
        fillMaterial: null,
      });
      renderStaticScene(true);
      return;
    }

    const img = await ensureLoadedImage(material.imageUrl, 'anonymous');

    element = elements.get(el.id) as GridElement;
    updateElement(
      useEditorStore.getState().elements,
      element,
      {
        showTestStructure: false,
        fillMaterial: {
          src: material.imageUrl,
          image: img,
          textureRealX: material.textureRealX,
          textureRealY: material.textureRealY,
        },
      },
      true,
    );
  },
  insertCornerLineAt: (
    grid: GridElement,
    segmentIndex: number,
    percentage: number,
  ) => {
    const [topPoint, bottomPoint] = getCornerLineAtPercentage(
      segmentIndex,
      percentage,
      grid,
    );

    const existingLines = grid.cornerLines ?? [];
    const newLine: [Point, Point] = [topPoint, bottomPoint];
    const newCornerLines: [Point, Point][] = [
      ...existingLines.slice(0, segmentIndex),
      newLine,
      ...existingLines.slice(segmentIndex),
    ];

    updateElement(
      useEditorStore.getState().elements,
      grid,
      {
        cornerLines: newCornerLines,
      },
      true,
    );
    gridState.cornerLinePreview = null;
    renderInteractiveScene();
  },
  resetCornerLines: (grid: GridElement) => {
    updateElement(
      useEditorStore.getState().elements,
      grid,
      {
        cornerLines: undefined,
      },
      true,
    );
  },
  deleteCornerLineAt: (grid: GridElement, lineIndex: number) => {
    const cornerLines = grid.cornerLines ?? [];
    if (lineIndex < 0 || lineIndex >= cornerLines.length) return;

    const newCornerLines = [
      ...cornerLines.slice(0, lineIndex),
      ...cornerLines.slice(lineIndex + 1),
    ];

    const normalized = normalizeGridToBounds(
      grid,
      grid.points,
      newCornerLines.length > 0 ? newCornerLines : undefined,
    );

    const elements = useEditorStore.getState().elements;
    updateElement(
      elements,
      grid,
      {
        ...normalized,
        ...reversionElement(grid, ['bounds', 'canvas', 'path']),
      },
      true,
    );
    invalidateAssociatedBoolean(grid.id, elements);
  },
};

const isInCornerAddMode = (runtimeState: {
  activeToolSubMode: string;
  activeKeyPress?: { shift?: boolean };
}) =>
  runtimeState.activeToolSubMode === 'grid_corner_add' ||
  runtimeState.activeKeyPress?.shift === true;

const isInCornerDeleteMode = (runtimeState: {
  activeToolSubMode: string;
  activeKeyPress?: { alt?: boolean };
}) =>
  runtimeState.activeToolSubMode === 'grid_corner_delete' ||
  runtimeState.activeKeyPress?.alt === true;

const gridEvents: GridPlugin['events'] = {
  onPointerMove: ({ event, elementState, helpers, runtimeState }) => {
    const inGridCornerMode =
      runtimeState.activeToolSubMode === 'grid_corner_add' ||
      runtimeState.activeToolSubMode === 'grid_corner_delete';

    if (
      runtimeState.activeTool !== 'selection' ||
      elementState.selectedElementIds.length !== 1
    ) {
      if (inGridCornerMode) {
        updateRuntimeStore({ activeToolSubMode: 'default' });
      }
      gridState.cornerLinePreview = null;
      gridState.cornerLineDeletePreview = null;
      helpers.renderInteractiveScene();
      return;
    }

    const grid = elementState.elements.get(
      elementState.selectedElementIds[0],
    ) as GridElement | undefined;
    if (!grid || grid.type !== 'grid') {
      if (inGridCornerMode) {
        updateRuntimeStore({ activeToolSubMode: 'default' });
      }
      gridState.cornerLinePreview = null;
      gridState.cornerLineDeletePreview = null;
      helpers.renderInteractiveScene();
      return;
    }

    const pos: Point = [event.pos.x, event.pos.y];

    if (isInCornerAddMode(runtimeState)) {
      gridState.cornerLineDeletePreview = null;
      const segmentIndex = getSegmentIndexAtPoint(grid, pos);
      if (segmentIndex < 0) {
        gridState.cornerLinePreview = null;
        helpers.renderInteractiveScene();
        return;
      }
      const percentage = getPercentageInSegment(segmentIndex, pos, grid);
      const [top, bottom] = getCornerLineAtPercentage(
        segmentIndex,
        percentage,
        grid,
      );
      gridState.cornerLinePreview = { gridId: grid.id, top, bottom };
    } else if (isInCornerDeleteMode(runtimeState)) {
      gridState.cornerLinePreview = null;
      const threshold = CORNER_LINE_DELETE_THRESHOLD / runtimeState.zoom.value;
      const lineIndex = getCornerLineIndexAtPoint(grid, pos, threshold);
      if (lineIndex >= 0) {
        gridState.cornerLineDeletePreview = {
          gridId: grid.id,
          lineIndex,
        };
      } else {
        gridState.cornerLineDeletePreview = null;
      }
    } else {
      gridState.cornerLinePreview = null;
      gridState.cornerLineDeletePreview = null;
    }

    helpers.renderInteractiveScene();
  },
  onPointerDown: ({ event, elementState, helpers, runtimeState }) => {
    if (
      event.button !== 'left' ||
      runtimeState.activeTool !== 'selection' ||
      elementState.selectedElementIds.length !== 1
    )
      return;

    const grid = elementState.elements.get(
      elementState.selectedElementIds[0],
    ) as GridElement | undefined;
    if (!grid || grid.type !== 'grid') return;

    const pos: Point = [event.pos.x, event.pos.y];

    if (isInCornerAddMode(runtimeState)) {
      const segmentIndex = getSegmentIndexAtPoint(grid, pos);
      if (segmentIndex < 0) return;

      const percentage = getPercentageInSegment(segmentIndex, pos, grid);
      gridActions.insertCornerLineAt(grid, segmentIndex, percentage);

      gridState.cornerLinePreview = null;
      if (runtimeState.activeToolSubMode === 'grid_corner_add') {
        updateRuntimeStore({ activeToolSubMode: 'default' });
      }
      history.push(updateEditorStore({}));
      renderStaticScene(true);
      helpers.renderInteractiveScene();
      return true;
    }

    if (isInCornerDeleteMode(runtimeState)) {
      const threshold = CORNER_LINE_DELETE_THRESHOLD / runtimeState.zoom.value;
      const lineIndex = getCornerLineIndexAtPoint(grid, pos, threshold);
      if (lineIndex < 0) return;

      gridActions.deleteCornerLineAt(grid, lineIndex);

      gridState.cornerLineDeletePreview = null;
      if (runtimeState.activeToolSubMode === 'grid_corner_delete') {
        updateRuntimeStore({ activeToolSubMode: 'default' });
      }
      history.push(updateEditorStore({}));
      renderStaticScene(true);
      helpers.renderInteractiveScene();
      return true;
    }

    return;
  },
  onKeyUp: ({ helpers }) => {
    const hadAddPreview = gridState.cornerLinePreview != null;
    gridState.cornerLinePreview = null;
    gridState.cornerLineDeletePreview = null;
    if (hadAddPreview) helpers.setCursor('auto');
  },
};

const plugin: GridPlugin = {
  name: 'grid',
  cursor: () => (gridState.cornerLinePreview != null ? 'col-resize' : null),
  actions: gridActions,
  state: gridState,
  events: gridEvents,
  render: {
    padding: (element) => element.strokeWidth,
    drawInteractiveOverlay: (context, { elementState, runtimeState }) => {
      const baseLineWidth = 2 / runtimeState.zoom.value;

      const addPreview = gridState.cornerLinePreview;
      if (addPreview) {
        const grid = elementState.elements.get(addPreview.gridId) as
          | GridElement
          | undefined;
        if (grid?.isVisible) {
          if (!elementState.selectedElementIds.includes(grid.id)) return;
          const { top, bottom } = addPreview;
          const x1 = grid.x + top[0];
          const y1 = grid.y + top[1];
          const x2 = grid.x + bottom[0];
          const y2 = grid.y + bottom[1];
          context.strokeStyle = 'red';
          context.lineWidth = baseLineWidth;
          context.setLineDash([4, 4]);
          context.beginPath();
          context.moveTo(x1, y1);
          context.lineTo(x2, y2);
          context.stroke();
          context.setLineDash([]);
        }
      }

      const deletePreview = gridState.cornerLineDeletePreview;
      if (deletePreview) {
        const grid = elementState.elements.get(deletePreview.gridId) as
          | GridElement
          | undefined;
        const line = grid?.cornerLines?.[deletePreview.lineIndex];
        if (grid?.isVisible && line) {
          if (!elementState.selectedElementIds.includes(grid.id)) return;

          const x1 = grid.x + line[0][0];
          const y1 = grid.y + line[0][1];
          const x2 = grid.x + line[1][0];
          const y2 = grid.y + line[1][1];
          context.strokeStyle = 'red';
          context.lineWidth = baseLineWidth * 2;
          context.setLineDash([]);
          context.beginPath();
          context.moveTo(x1, y1);
          context.lineTo(x2, y2);
          context.stroke();
        }
      }
    },
    draw: ({
      context,
      element: el,
      runtimeState,
      elementState,
      skipElementUpdate,
    }) => {
      const element = el as GridElement;
      if (
        (!runtimeState.newElement && !element.points.length) ||
        !element.isVisible
      )
        return;

      context.save();

      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth =
        element.strokeWidth / 2 / Math.min(1, runtimeState.zoom.value);
      context.strokeStyle = element.strokeColor;

      const path = getPathFromGrid(element);
      context.stroke(path);

      if (!skipElementUpdate) {
        updateElement<GenericPolyElement>(elementState.elements, element, {
          path,
        });
      }

      if (!element.hideDefaultGrid) {
        context.lineWidth = 1;
        const lineCount = Math.round(
          15 * Math.max(0.25, Math.min(2, runtimeState.zoom.value)),
        );
        if (element.showTestStructure) {
          context.strokeStyle = hexToRgbString(element.strokeColor, 0.5);
        }
        if (!element.cornerLines?.length) {
          const gridPath = drawGrid(element.points, lineCount);
          context.stroke(gridPath);
        } else {
          const { points, cornerLines } = element;

          const getSegmentBoundary = (i: number) => ({
            leftTop: i === 0 ? points[0] : cornerLines[i - 1][0],
            leftBottom: i === 0 ? points[3] : cornerLines[i - 1][1],
            rightTop: i === cornerLines.length ? points[1] : cornerLines[i][0],
            rightBottom:
              i === cornerLines.length ? points[2] : cornerLines[i][1],
          });

          const segments = Array.from(
            { length: cornerLines.length + 1 },
            (_, i) => {
              const { leftTop, rightTop, rightBottom, leftBottom } =
                getSegmentBoundary(i);
              return [leftTop, rightTop, rightBottom, leftBottom];
            },
          );
          segments.forEach((segmentPoints) => {
            context.stroke(drawGrid(segmentPoints, lineCount));
          });
        }
      }

      if (element.cornerLines) {
        element.cornerLines.forEach((cornerLine) => {
          context.strokeStyle = 'red';
          context.stroke(getPathFromPoints(cornerLine, undefined, false));
        });
      }

      context.restore();
    },
    translate: poly.render.translate,
  },
  collision: {
    ...poly.collision,
    bounds: (element) => getGridBounds(element as GridElement),
  },
  transformation: {
    ...poly.transformation,
    resizeTestFromBounds: (element, [cursorX, cursorY], lib) => {
      const handles = getGridTransformHandles(element as GridElement, lib);
      const found = Object.keys(handles).find((key) =>
        isInsideTransformHandle(handles[key], cursorX, cursorY),
      );
      return found ?? null;
    },
    transformElement: async (
      transform,
      element,
      elements,
      pointer,
      origin,
      helpers,
    ) => {
      const gridElement = element as GridElement;
      const match = transform.match(/^cornerLine-(\d+)-(\d+)$/);

      if (match) {
        const [, lineIndex, pointIndex] = match.map(Number);
        const originalElement = elements.get(element.id) as GridElement;
        const originalPoint =
          originalElement?.cornerLines?.[lineIndex]?.[pointIndex];
        if (!originalPoint) return;

        const [sceneDx, sceneDy] = [
          pointer[0] - origin[0],
          pointer[1] - origin[1],
        ];
        const [dx, dy] = pointRotateRads(
          [sceneDx, sceneDy],
          [0, 0],
          normalizeRadians(-originalElement.angle),
        );

        const pointsInOriginalSpace = originalElement.points.map(
          (point) => [point[0], point[1]] as Point,
        );
        const newCornerLines = originalElement.cornerLines!.map((line, i) => {
          const newLine = line.map(
            (point) => [point[0], point[1]] as Point,
          ) as [Point, Point];
          if (i === lineIndex) {
            newLine[pointIndex] = [
              originalPoint[0] + dx,
              originalPoint[1] + dy,
            ];
          }
          return newLine;
        });

        const normalized = normalizeGridToBounds(
          originalElement,
          pointsInOriginalSpace,
          newCornerLines,
        );

        updateElement(helpers.elementState.elements, gridElement, {
          ...normalized,
          shape: getPolyShape({ ...gridElement, ...normalized }),
          ...reversionElement(gridElement, ['bounds', 'canvas', 'path']),
        });
        invalidateAssociatedBoolean(
          gridElement.id,
          helpers.elementState.elements,
        );
        return;
      }

      if (['0', '1', '2', '3'].includes(transform)) {
        const cornerIndex = Number.parseInt(transform, 10);
        const originalElement = elements.get(element.id) as GridElement;
        const updated = translateGridCorner(
          cornerIndex,
          originalElement,
          pointer,
          origin,
        );
        updateElement(helpers.elementState.elements, gridElement, {
          ...updated,
          ...reversionElement(gridElement, ['bounds', 'canvas', 'path']),
          shape: getPolyShape({ ...gridElement, ...updated }),
        });
        invalidateAssociatedBoolean(
          gridElement.id,
          helpers.elementState.elements,
        );
        return;
      }

      poly.transformation!.transformElement!(
        transform,
        element,
        elements,
        pointer,
        origin,
        helpers,
      );

      invalidateAssociatedBoolean(
        gridElement.id,
        helpers.elementState.elements,
      );
    },
  },
  selection: {
    ...poly.selection,
    renderSelectionBorder: () => undefined,
    renderHoverBorder: () => undefined,
    hideDefaultTransformHandles: true,
    hasVisiblePointHandles: false,
    getTransformHandles: (element, helpers) => {
      return getGridTransformHandles(element as GridElement, helpers);
    },
  },
  export: poly.export,
  import: async (el) => {
    const element = {
      ...defaultGenericElement(),
      ...el,
      showTestStructure: false,
      bezierHandles: undefined,
    } as GridElement;

    const { fillMaterial } = element as GridElement;
    if (fillMaterial?.src) {
      const img = await ensureLoadedImage(fillMaterial.src, 'anonymous');

      element.fillMaterial = { ...fillMaterial, image: img };
    }
    element.shape = getPolyShape(element);
    element.layer = Number.MAX_SAFE_INTEGER;

    return element;
  },
};

let timerId: number | null = null;

const invalidateAssociatedBoolean = (gridId: string, elements: ElementMap) => {
  const booleanId = gridId.startsWith('grid-') ? gridId.slice(5) : null;
  if (!booleanId) return;
  const booleanEl = elements.get(booleanId);
  if (booleanEl?.type === 'boolean') {
    if (timerId) clearTimeout(timerId);

    timerId = window.setTimeout(() => {
      timerId = null;
      updateElement(
        elements,
        booleanEl,
        reversionElement(booleanEl, ['canvas']),
        true,
      );
    }, 500);
  }
};

export const grid = plugin;
